import { decodeOneOf, encodeOneOf, METRIC_CODES, safePage, safeSort, safeText } from '../../../domain';
import { PATIENT_PERIOD_CODES } from '../../../components/patient-analytics';
import { periodRange } from '../../../components/period';
import { MainKeys } from '../../main/main.key';
import { Places } from '../../route-consts';
import { READING_SORT_FIELDS } from '../../generic/patient-detail/history-table';
/** Prefixo dos nomes deste arquivo na intencao - unico na aplicacao (ver `url-codes.test.ts`). */
const PREFIX = 'sh';
/** Os parametros deste place: vao para o endereco. */
const P = {
    ShareCode: `${PREFIX}a`,
    /** Presente quando o link tambem vincula o paciente ao profissional. */
    LinkPatient: `${PREFIX}b`,
    /** Qual visao do historico compartilhado: ausente e o grafico, `1` e a lista. */
    View: `${PREFIX}c`,
    SharedPeriod: `${PREFIX}d`,
    SharedFrom: `${PREFIX}e`,
    SharedTo: `${PREFIX}f`,
    SharedMetric: `${PREFIX}g`,
    HistoryListSort: `${PREFIX}h`,
    HistoryListPage: `${PREFIX}i`
};
/**
 * Um historico que um paciente compartilhou por link.
 *
 * Herda de `MainKeys` e nao de `RestrictedKeys`: esta tela e publica, fica fora do
 * HUB, e nao tem sessao a pedir.
 */
export class SharedHistoryKeys extends MainKeys {
    get place() {
        return Places.sharedHistory;
    }
    /**
     * A query de um link de compartilhamento que ja esta na rua
     * (`{site}/shared-history/{uuid}`), em texto claro. A ponte do arranque, no
     * renderizador, a sela e a poe no endereco - sem conhecer o nome dos parametros.
     */
    static legacyQuery(code, linkPatient) {
        return `${P.ShareCode}=${encodeURIComponent(code)}` + (linkPatient ? `&${P.LinkPatient}=1` : '');
    }
    /** O identificador do compartilhamento - um UUID gerado pelo backend. */
    get code() {
        return safeText(this._intent.getParameterAsString(P.ShareCode));
    }
    set code(value) {
        this._intent.setParameter(P.ShareCode, value ? value : undefined);
    }
    /**
     * Se o link tambem vincula o paciente ao profissional que o abriu.
     *
     * O backend gera DOIS enderecos: `shared-history/{id}` so mostra, e
     * `shared-full-history/{id}` mostra e vincula (ver `MeasurementHistoryService`).
     * Aqui e um parametro em vez de dois places, porque e a mesma tela - o que muda
     * e um efeito na chegada.
     */
    get linkPatient() {
        return this._intent.getParameterAsString(P.LinkPatient) === '1';
    }
    set linkPatient(value) {
        this._intent.setParameter(P.LinkPatient, value ? '1' : undefined);
    }
    // :: painel de analytics
    /**
     * Qual visao: o painel ou a lista de medicoes.
     *
     * O padrao e o PAINEL, e por isso e ele que fica ausente do endereco: na
     * referencia o `showDetails` comeca falso, e quem abre o link ve os graficos.
     */
    get view() {
        return this._intent.getParameterAsString(P.View) === '1' ? 'list' : 'chart';
    }
    set view(value) {
        this._intent.setParameter(P.View, value === 'list' ? '1' : undefined);
    }
    /**
     * Se o periodo veio ESCRITO no endereco.
     *
     * Ausente, a tela escolhe o padrao - e o padrao depende do link: os ultimos 7 dias
     * de HOJE para um link que ainda cobre hoje, os 7 ultimos do link para um que
     * terminou antes (ver `SharedHistoryPresenter.fitPeriod`).
     */
    get periodChosen() {
        return this._intent.getParameterAsString(P.SharedPeriod) !== undefined;
    }
    get period() {
        return decodeOneOf(this._intent.getParameterAsString(P.SharedPeriod), PATIENT_PERIOD_CODES, '7d');
    }
    set period(value) {
        this._intent.setParameter(P.SharedPeriod, value === '7d' ? undefined : encodeOneOf(value, PATIENT_PERIOD_CODES));
    }
    /**
     * As pontas do intervalo - lidas do atalho, ou do endereco quando ele e `custom`.
     *
     * So o `custom` as ESCREVE: nos outros o intervalo e derivado do atalho, e
     * repetir na URL o que o atalho ja diz seria ruido - e pior, poderia discordar
     * dele depois que o dia virasse.
     */
    get analyticsFrom() {
        return this._intent.getParameterAsString(P.SharedFrom) ?? periodRange(this.period).from;
    }
    set analyticsFrom(value) {
        this._intent.setParameter(P.SharedFrom, this.period === 'custom' ? value : undefined);
    }
    get analyticsTo() {
        return this._intent.getParameterAsString(P.SharedTo) ?? periodRange(this.period).to;
    }
    set analyticsTo(value) {
        this._intent.setParameter(P.SharedTo, this.period === 'custom' ? value : undefined);
    }
    get metric() {
        return decodeOneOf(this._intent.getParameterAsString(P.SharedMetric), METRIC_CODES, 'FREQUENCIA_CARDIACA');
    }
    set metric(value) {
        this._intent.setParameter(P.SharedMetric, value === 'FREQUENCIA_CARDIACA' ? undefined : encodeOneOf(value, METRIC_CODES));
    }
    // :: tabela
    /** A coluna pela qual a tabela esta ordenada, com `-` para descendente. */
    get sort() {
        return safeSort(this._intent.getParameterAsString(P.HistoryListSort), READING_SORT_FIELDS);
    }
    set sort(value) {
        this._intent.setParameter(P.HistoryListSort, value ? value : undefined);
    }
    get page() {
        return safePage(this._intent.getParameterAsNumber(P.HistoryListPage));
    }
    set page(value) {
        this._intent.setParameter(P.HistoryListPage, value > 1 ? value : undefined);
    }
}
//# sourceMappingURL=shared-history.key.js.map